import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeleteAccount = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const handleDelete = async () => {
    const confirmed = window.confirm(
      'This permanently deletes your PropTick account, your saved properties, the enquiries you have sent, and every property you have listed, including its photos. This cannot be undone.'
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user/me`, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      alert('Your account has been deleted.');
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-16" data-testid="delete-account-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-8">Delete your PropTick account</h1>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6 text-slate-700 leading-relaxed">
          <p>You can delete your PropTick account and all data connected to it at any time.</p>

          <div>
            <h3 className="font-semibold text-slate-900 mb-2">In the app</h3>
            <p>Open the Account tab, scroll to the bottom and tap <strong>Delete account</strong>.</p>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-2">On the website</h3>
            <p>Sign in, open <strong>My Dashboard</strong>, scroll to the bottom and click <strong>Delete account</strong>.</p>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-2">By email</h3>
            <p>
              If you no longer have access to the app or your login, email{' '}
              <a href="mailto:admin@proptick.in" className="text-blue-600 hover:underline">admin@proptick.in</a>{' '}
              from the email address or mobile number on the account with the subject "Delete my account".
              We will verify the request and complete the deletion within 30 days.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h3 className="font-semibold text-slate-900 mb-2">What gets deleted</h3>
            <p>
              Deleting your account removes your profile, saved properties, the enquiries you have sent,
              and the properties you have listed together with their photos. Deletion is completed within
              30 days, after which the data is also removed from backups. We may keep records we are legally
              required to retain and usage statistics that are no longer linked to you. See our{' '}
              <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a> for details.
            </p>
          </div>

          {user && (
            <div className="border-t border-slate-200 pt-6">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-all disabled:opacity-50"
                data-testid="delete-account-now-btn"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete my account now'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
